import axios from "axios";
import * as jsonwebtoken from "jsonwebtoken";

import {
    LocalStorageAuthorizationData,
    LocalStoredAuthorizationConfigRequest
} from "./localstorage-authorization-data";
import {
    LocalStoredAuthorizationSetting,
    IAuthorizationSetting
} from "./localstorage-auth-setting";
import {
    LOCALSTORAGE_USERSSION,
    APICONFIG_HEADER_AUTHORIZATION,
    APICONFIG_HEADER_SESSIONID,
    LOCALSTORAGE_AUTH,
    LOCALSTORAGE_AUTH2,
    LOCALSTORAGE_MAP_OPTIONS,
    LOCALSTORAGE_MAP_REGIONS,
    APICONFIG_HEADER_LANGUAGE,
    AUTH_HEADER_MODE,
    APICONFIG_HEADER_MASTER_USER,
    LOCALSTORAGE_MASTER_USER,
    SERVICE_LINKS,
    LOCALSTORAGE_AI_TUTORIAL,
    LOCALSTORAGE_AI_IMAGE_GENERATION_INTRODUCTION
} from "../../common/constants";
import { ApiResponse } from "../../models/app-configuration";
import { AuthVerified } from "../../models/generic/auth-transfer";
import {
    AuthorizationResponse,
    AuthorizationResponseStatus
} from "./data-model";
import authorizationService from "./service";

const clearAuthorizationData = () => {
    delete axios.defaults.headers;
    localStorage.removeItem(LOCALSTORAGE_AUTH);
    localStorage.removeItem(LOCALSTORAGE_AUTH2);
    localStorage.removeItem(LOCALSTORAGE_USERSSION);
    localStorage.removeItem(LOCALSTORAGE_MAP_OPTIONS);
    localStorage.removeItem(LOCALSTORAGE_MAP_REGIONS);
};
const setAuthorizationDataToStorageAndRequest = (response, authSetting) => {
    let authorizationResponse = new AuthorizationResponse();
    const payload = jsonwebtoken.decode(response.oauth2.identityToken, {
        json: true
    }) as any;
    authorizationResponse.Status = AuthorizationResponseStatus.Success;
    authorizationResponse.UserInfo = {
        Token: response.oauth2.accessToken,
        RefreshToken: response.oauth2.refreshToken,
        OriginDomain: response.originDomain,
        SessionId: response.information.sessionId,
        UserInfo: {
            Id: payload.userId,
            SiteId: payload.siteId,
            SiteName: payload.siteName,
            CompanyName: payload.companyName,
            Name: payload.userName,
            ShortName: payload.shortName,
            MobilePhone: payload.mobilePhone,
            Email: payload.email,
            DepartmentId: payload.departmentId,
            DirectPhone: payload.directPhone,
            IsActive: payload.isActived,
            GroupId: payload.groupId
        }
    };
    const authInfo = response.information;
    authorizationResponse.Config = {
        authApiEndPoint: authInfo.authApiEndPoint,
        apiEndPoint: authInfo.webApiEndPoint,
        apiVersion: authInfo.webApiVersion,
        version: authInfo.webAppVersion,
        originDomain: authInfo.originDomain,
        code: authInfo.languageCode,
        token: response.oauth2.accessToken,
        environment: authInfo.environment,
        sessionId: authInfo.sessionId
    };

    localStorage.setItem(
        LOCALSTORAGE_USERSSION,
        JSON.stringify(authorizationResponse.UserInfo)
    );
    localStorage.setItem(
        LOCALSTORAGE_AUTH,
        JSON.stringify(authorizationResponse.Config)
    );

    if (!!authorizationResponse.Config.apiVersion)
        axios.defaults.baseURL = `//${authInfo.webApiEndPoint}/${authInfo.webApiVersion}`;
    else axios.defaults.baseURL = `//${authInfo.webApiEndPoint}`;

    axios.defaults.headers.common[APICONFIG_HEADER_AUTHORIZATION] =
        "Bearer " + response.oauth2.accessToken;
    axios.defaults.headers.common[APICONFIG_HEADER_LANGUAGE] = (
        window["webViewLang"] || authInfo.languageCode
    ).toLocaleUpperCase();
    axios.defaults.headers.common[APICONFIG_HEADER_SESSIONID] =
        authInfo.sessionId || "";
    axios.defaults.headers.common[AUTH_HEADER_MODE] =
        authSetting.AuthorizationMode;

    const metaWebApiVersion = document.querySelector(
        "meta[name='WebApi-Version']"
    );
    if (metaWebApiVersion) {
        axios.defaults.baseURL =
            "//" +
            authInfo.webApiEndPoint +
            "/" +
            metaWebApiVersion.getAttribute("content");
    }
    return authorizationResponse;
};
const getUrl = (url: string) => {
    if (url == null) return null;
    if (url.indexOf("://") >= 0) return url;
    return "//" + url;
};

const gotoLoginPage = url => {
    const loginUrlEl = document.querySelector("meta[name='Login-Url']");
    if (loginUrlEl) {
        location.href = loginUrlEl.getAttribute("content");
    } else {
        let loginUrl =
            getUrl(url) ||
            "//" +
            location.hostname +
            (location.port ? ":" + location.port : "");
        location.href = loginUrl;
    }
};
export class AuthorizationHandlerFactory {
    authorizationData: any;
    authorizationSetting: IAuthorizationSetting;
    constructor(version?: any) {
        this.authorizationData = new LocalStorageAuthorizationData().authorizationData;

        //WA-3578: Check upgrade version web api, update local storage
        if (version) {
            this.authorizationData.information.webApiVersion = version;
        }
        this.authorizationSetting = new LocalStoredAuthorizationSetting().getAuthorizationSetting();
    }
    authenticationHandler() {
        return new Promise((resolve, reject) => {
            if (this.authorizationData) {
                // handle set info to storage and axios header
                const authorizationFullInfo = setAuthorizationDataToStorageAndRequest(
                    this.authorizationData,
                    this.authorizationSetting
                );
                resolve(authorizationFullInfo);
            } else {
                // handle clear storage and go to login page.
                clearAuthorizationData();
                gotoLoginPage(this.authorizationSetting.LoginUrl);
                reject();
            }
        });
    }
}

export class WebviewAuthorizationHandlerFactory {
    webviewToken: string = null;
    webviewRefreshToken: string = null;
    authorizationSetting: IAuthorizationSetting;
    authorizationDataHandler;
    constructor(token: string, refreshToken?: string) {
        this.webviewToken = token;
        this.webviewRefreshToken = refreshToken;
        this.authorizationSetting = new LocalStoredAuthorizationSetting().getAuthorizationSetting();
        this.authorizationDataHandler = new LocalStorageAuthorizationData();
    }
    verifyToken(query = {}) {
        return authorizationService
            .verifyWebviewToken(this.webviewToken, query)
            .then((response: any) => {
                if (query["is-master"] != undefined) {
                    Object.assign(response, {
                        [LOCALSTORAGE_MASTER_USER]: query["is-master"]
                    });
                }

                // Merge refresh token: prioritize URL > localStorage > API response
                // This ensures refresh token is preserved when opening new webviews
                const existingAuthData = this.authorizationDataHandler.getAuthorization();
                const existingRefreshToken = existingAuthData?.oauth2?.refreshToken;

                if (response.data?.oauth2) {
                    // Priority: URL > localStorage > API response
                    if (this.webviewRefreshToken) {
                        response.data.oauth2.refreshToken = this.webviewRefreshToken;
                    } else if (existingRefreshToken) {
                        // localStorage takes precedence over API response
                        response.data.oauth2.refreshToken = existingRefreshToken;
                    }
                }

                this.authorizationDataHandler.setAuthorization(response);
                const authorizationFullInfo = setAuthorizationDataToStorageAndRequest(
                    response.data,
                    this.authorizationSetting
                );
                return Promise.resolve(authorizationFullInfo);
            })
            .catch(err => {
                // toto handle go to unauthorization page.
                return Promise.reject(err);
            });
    }
}
export class LogoutCommandHandlerFactory {
    storedAuthorizationConfigRequest;
    setting;
    constructor() {
        this.setting = new LocalStoredAuthorizationSetting().getAuthorizationSetting();
        this.storedAuthorizationConfigRequest = new LocalStoredAuthorizationConfigRequest();
    }
    Logout(): Promise<any> {
        const authorizationConfigReques = this.storedAuthorizationConfigRequest
            .AuthConfig;
        const userSession: AuthVerified = JSON.parse(
            localStorage.getItem(LOCALSTORAGE_USERSSION)
        );
        delete axios.defaults.headers.common[APICONFIG_HEADER_MASTER_USER];
        return axios
            .get(SERVICE_LINKS.AUTH_WEB_LOGOUT_URL, {
                headers: {
                    [APICONFIG_HEADER_AUTHORIZATION]:
                        "Bearer " + authorizationConfigReques.token,
                    [APICONFIG_HEADER_SESSIONID]: userSession.SessionId || ""
                }
            })
            .then((response: ApiResponse) => {
                localStorage.setItem(LOCALSTORAGE_AI_TUTORIAL, "false");
                localStorage.setItem(LOCALSTORAGE_AI_IMAGE_GENERATION_INTRODUCTION, "false");
                clearAuthorizationData();

                // const that = window as any
                // // Clear New Relic
                // that.newrelic.setCustomAttribute('userId', '');
                // that.newrelic.setCustomAttribute('userEmail', '');
                // that.newrelic.setCustomAttribute('userName', '');
                // that.newrelic.setUserId(null);
                // that.newrelic.interaction().end();

                setTimeout(() => {
                    gotoLoginPage(this.setting.LoginUrl);
                }, 300);
            })
            .catch(error => {
                Promise.reject(error);
            });
    }
}

export const LocalStoredOAuthorization2Response = LocalStorageAuthorizationData;
export { LocalStoredAuthorizationSetting } from "./localstorage-auth-setting";
